import type { MusicGenerationPrefs, MusicalToken, RhythmReadingMethod, TimeSignature } from '../../types/music';
import { CHILEAN_FIGURE_LABEL } from '../tokens';
import type { ParsedScoreAst, ParsedScoreBarSummary, ParsedScoreSummary, ScoreMeasure, ScoreNoteEvent } from './scoreAst.types';
import { pitchToSpanishLabel } from './quantize';

const TAKADIMI: Record<MusicalToken, string> = {
  W: 'Ta-a-a-a',
  H: 'Ta-a',
  Q: 'Ta',
  E: 'Ta-di',
  S: 'Ta-ka-di-mi',
  R_Q: 'Silencio',
};

const KODALY: Record<MusicalToken, string> = {
  W: 'ta-a-a-a',
  H: 'ta-a',
  Q: 'Ta',
  E: 'ti-ti',
  S: 'ti-ki-ti-ki',
  R_Q: 'silencio',
};

const KEY_NAMES: Record<number, string> = {
  0: 'Do Mayor',
  1: 'Sol Mayor (1 sostenido)',
  2: 'Re Mayor (2 sostenidos)',
  3: 'La Mayor (3 sostenidos)',
  4: 'Mi Mayor (4 sostenidos)',
  5: 'Si Mayor (5 sostenidos)',
  [-1]: 'Fa Mayor (1 bemol)',
  [-2]: 'Si bemol Mayor (2 bemoles)',
  [-3]: 'Mi bemol Mayor (3 bemoles)',
};

function pitchToMidi(pitch: string): number {
  const m = String(pitch || 'c/4').match(/^([a-g])([#b]?)\/(\d+)/i);
  if (!m) return 60;
  const step = m[1].toLowerCase();
  const acc = m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0;
  const octave = Number(m[3]);
  const base: Record<string, number> = { c: 0, d: 2, e: 4, f: 5, g: 7, a: 9, b: 11 };
  return (octave + 1) * 12 + (base[step] ?? 0) + acc;
}

export function pitchToIsoName(pitch: string): string {
  const m = String(pitch || 'c/4').match(/^([a-g])([#b]?)\/(\d+)/i);
  if (!m) return 'C4';
  const letter = m[1].toUpperCase();
  const acc = m[2] || '';
  return `${letter}${acc}${m[3]}`;
}

export function isoToChileanOctave(iso: string): string {
  const m = String(iso || 'C4').match(/^([A-Ga-g])(#|b)?(\d)$/);
  if (!m) return iso;
  const acc = m[2] === '#' ? ' sostenido' : m[2] === 'b' ? ' bemol' : '';
  return `${pitchToSpanishLabel(`${m[1].toLowerCase()}/4`)}${acc} ${m[3]}`.trim();
}

export function pitchToChileanOctave(pitch: string): string {
  const m = String(pitch || 'c/4').match(/^([a-g])([#b]?)\/(\d+)/i);
  if (!m) return isoToChileanOctave(pitch);
  const acc = m[2] === '#' ? ' sostenido' : m[2] === 'b' ? ' bemol' : '';
  return `${pitchToSpanishLabel(`${m[1]}/4`)}${acc} ${m[3]}`.trim();
}

function figureLabel(token: MusicalToken, method: RhythmReadingMethod = 'tradicional'): string {
  if (method === 'takadimi') return TAKADIMI[token];
  if (method === 'kodaly') return KODALY[token];
  return CHILEAN_FIGURE_LABEL[token];
}

function eventFigureText(ev: ScoreNoteEvent, method: RhythmReadingMethod = 'tradicional'): string {
  let label = figureLabel(ev.token, method);
  if (ev.symbols?.includes('puntillo')) label += ' con puntillo';
  if (ev.symbols?.includes('ligadura de prolongación')) label += ' (ligadura)';
  return label;
}

function eventsToText(events: ScoreNoteEvent[], method: RhythmReadingMethod): string {
  return events.map((ev) => eventFigureText(ev, method)).join(', ');
}

function tokensToText(tokens: MusicalToken[], method: RhythmReadingMethod): string {
  return tokens.map((t) => figureLabel(t, method)).join(', ');
}

function expectedBarBeats(timeSignature: TimeSignature): number {
  const [n, d] = timeSignature.split('/').map(Number);
  return (n || 4) * (4 / (d || 4));
}

function isMetricallyCompleteBar(
  bar: ParsedScoreBarSummary | undefined,
  timeSignature: TimeSignature,
): boolean {
  if (!bar?.tokens?.length) return false;
  const expected = expectedBarBeats(timeSignature);
  const total = Number(bar.totalBeats ?? 0);
  return total > 0 && Math.abs(total - expected) < 0.06;
}

function inferKeySignature(measures: ScoreMeasure[]): string {
  let sharpScore = 0;
  let flatScore = 0;
  for (const measure of measures) {
    for (const ev of measure.events) {
      if (ev.isRest) continue;
      if (ev.pitch.includes('#')) sharpScore += 1;
      if (ev.pitch.includes('b')) flatScore += 1;
    }
  }
  if (sharpScore === 0 && flatScore === 0) return KEY_NAMES[0];
  if (sharpScore >= flatScore) {
    const idx = Math.min(sharpScore, 5);
    return KEY_NAMES[idx] ?? KEY_NAMES[1];
  }
  const idx = Math.max(-3, -Math.min(flatScore, 3));
  return KEY_NAMES[idx] ?? KEY_NAMES[-1];
}

function inferClef(measures: ScoreMeasure[]): 'G' | 'F' {
  const midis: number[] = [];
  for (const measure of measures) {
    for (const ev of measure.events) {
      if (!ev.isRest) midis.push(pitchToMidi(ev.pitch));
    }
  }
  if (!midis.length) return 'G';
  const avg = midis.reduce((a, b) => a + b, 0) / midis.length;
  return avg >= 55 ? 'G' : 'F';
}

function detectSyncopation(events: ScoreNoteEvent[], timeSignature: TimeSignature): boolean {
  const [beatsPerBar] = timeSignature.split('/').map(Number);
  for (const ev of events) {
    if (ev.isRest) continue;
    const beatMod = ev.beat % 1;
    const onOffBeat = Math.abs(beatMod - 0.5) < 0.06;
    const short = ev.durationBeats <= 1;
    if (onOffBeat && short && ev.beat < beatsPerBar) return true;
  }
  return false;
}

function barComplexity(events: ScoreNoteEvent[], timeSignature: TimeSignature): number {
  const sync = detectSyncopation(events, timeSignature) ? 3 : 0;
  const density = events.filter((e) => !e.isRest).length;
  const unique = new Set(events.map((e) => e.token)).size;
  return density + sync + unique;
}

function barNoteExtremes(events: ScoreNoteEvent[]): { highest: string; lowest: string } {
  const notes = events.filter((e) => !e.isRest);
  if (!notes.length) return { highest: '—', lowest: '—' };
  let hi = notes[0];
  let lo = notes[0];
  for (const n of notes) {
    if (pitchToMidi(n.pitch) > pitchToMidi(hi.pitch)) hi = n;
    if (pitchToMidi(n.pitch) < pitchToMidi(lo.pitch)) lo = n;
  }
  return {
    highest: pitchToChileanOctave(hi.pitch),
    lowest: pitchToChileanOctave(lo.pitch),
  };
}

function collectMelodicRange(measures: ScoreMeasure[]): { min: string; max: string } {
  let minMidi = Infinity;
  let maxMidi = -Infinity;
  let minPitch = 'c/4';
  let maxPitch = 'c/4';
  for (const measure of measures) {
    for (const ev of measure.events) {
      if (ev.isRest) continue;
      const midi = pitchToMidi(ev.pitch);
      if (midi < minMidi) {
        minMidi = midi;
        minPitch = ev.pitch;
      }
      if (midi > maxMidi) {
        maxMidi = midi;
        maxPitch = ev.pitch;
      }
    }
  }
  if (!Number.isFinite(minMidi)) return { min: 'C4', max: 'C4' };
  return { min: pitchToIsoName(minPitch), max: pitchToIsoName(maxPitch) };
}

function collectSymbols(measures: ScoreMeasure[]): {
  present: string[];
  byBar: Array<{ symbol: string; bar: number }>;
} {
  const byBar: Array<{ symbol: string; bar: number }> = [];
  const seen = new Set<string>();
  const present: string[] = [];
  for (const measure of measures) {
    const barNum = measure.index + 1;
    for (const ev of measure.events) {
      for (const sym of ev.symbols ?? []) {
        byBar.push({ symbol: sym, bar: barNum });
        if (!seen.has(sym)) {
          seen.add(sym);
          present.push(sym);
        }
      }
    }
  }
  return { present, byBar };
}

function mostUsedFigure(measures: ScoreMeasure[]): string {
  const counts = new Map<MusicalToken, number>();
  for (const measure of measures) {
    for (const ev of measure.events) {
      if (ev.isRest) continue;
      counts.set(ev.token, (counts.get(ev.token) ?? 0) + 1);
    }
  }
  let best: MusicalToken = 'Q';
  let bestCount = -1;
  for (const [token, count] of counts) {
    if (count > bestCount) {
      bestCount = count;
      best = token;
    }
  }
  return CHILEAN_FIGURE_LABEL[best];
}

function extractTiePairs(events: ScoreNoteEvent[]): [number, number][] {
  const pairs: [number, number][] = [];
  let pendingStart: number | null = null;
  for (let i = 0; i < events.length; i++) {
    const ev = events[i];
    if (ev.tieStop && pendingStart != null) {
      pairs.push([pendingStart, i]);
      pendingStart = null;
    }
    if (ev.tieStart) pendingStart = i;
  }
  return pairs;
}

export function analyzeScoreFromAst(
  ast: ParsedScoreAst,
  prefs?: Pick<MusicGenerationPrefs, 'readingMethod'>,
): ParsedScoreSummary {
  const method = prefs?.readingMethod ?? 'tradicional';
  const measures = ast.measures.length ? ast.measures : [];
  const bars: Record<number, ParsedScoreBarSummary> = {};
  let hardestBar = 1;
  let hardestScore = -1;

  for (const measure of measures) {
    const barNum = measure.index + 1;
    const events = measure.events;
    const tokens = events.map((e) => e.token);
    const totalBeats = events.reduce((s, e) => s + (e.durationBeats || 0), 0);
    const dottedIndices = events
      .map((e, i) => (e.symbols?.includes('puntillo') ? i : -1))
      .filter((i) => i >= 0);
    const tiePairs = extractTiePairs(events);
    const { highest, lowest } = barNoteExtremes(events);
    const complexity = barComplexity(events, measure.timeSignature);
    if (complexity > hardestScore) {
      hardestScore = complexity;
      hardestBar = barNum;
    }
    bars[barNum] = {
      tokens,
      textRepresentation: eventsToText(events, method),
      totalBeats,
      dottedIndices,
      tiePairs,
      hasSyncopation: detectSyncopation(events, measure.timeSignature),
      highestNote: highest,
      lowestNote: lowest,
    };
  }

  if (!measures.length) {
    bars[1] = {
      tokens: ast.primaryPattern,
      textRepresentation: tokensToText(ast.primaryPattern, method),
      totalBeats: expectedBarBeats(ast.timeSignature),
      dottedIndices: [],
      tiePairs: [],
      hasSyncopation: false,
      highestNote: '—',
      lowestNote: '—',
    };
  }

  const { present, byBar } = collectSymbols(measures);

  return {
    title: ast.title,
    timeSignature: ast.timeSignature,
    keySignature: inferKeySignature(measures),
    keyFifths: typeof ast.keyFifths === 'number' ? ast.keyFifths : undefined,
    clef: inferClef(measures),
    totalBars: Math.max(measures.length, 1),
    bpm: ast.bpm,
    source: ast.source,
    bars,
    metrics: {
      mostUsedFigure: mostUsedFigure(measures),
      hardestBarNumber: hardestBar,
      musicalSymbolsPresent: present,
      symbolByBar: byBar,
      melodicRange: collectMelodicRange(measures),
    },
  };
}

/** Prompt de coacción estricta — la IA solo puede usar evidencia del reporte. */
export function buildScoreEvidencePrompt(
  summary: ParsedScoreSummary,
  prefs?: Pick<MusicGenerationPrefs, 'readingMethod'>,
): string {
  const method = prefs?.readingMethod ?? 'tradicional';
  const hardestNum = summary.metrics.hardestBarNumber;
  const hardest = summary.bars[hardestNum];
  const rotationPreview = getBarRotationOrder(summary).slice(0, 8).join(', ');
  const symbolLines = summary.metrics.symbolByBar.length
    ? summary.metrics.symbolByBar.map((s) => `  · ${s.symbol} (compás ${s.bar})`).join('\n')
    : '  · (ninguno detectado en el archivo)';

  return [
    '[REPORTE TÉCNICO DE LA PARTITURA ENTRANTE]',
    JSON.stringify(summary, null, 2),
    '',
    '[REGLAS DE CONSTRUCCIÓN DIDÁCTICA — OBLIGATORIAS]',
    '1. Bloques rítmicos (completar, rhythm_canvas, order, dictado): usa compases REALES de bars[].',
    `   · El PRIMER bloque rítmico debe usar el compás ${hardestNum} (metrics.hardestBarNumber — el más complejo).`,
    `     Tokens: [${hardest?.tokens?.join(', ') ?? '?'}] · ${hardest?.textRepresentation ?? '—'}`,
    '   · Los demás bloques rítmicos: asigna targetBarNumber distinto recorriendo bars (sin repetir compás hasta agotar la lista).',
    `     Orden sugerido de rotación: ${rotationPreview}${summary.totalBars > 8 ? '…' : ''}`,
    '   · patternTokens y originalBarTokens deben ser EXACTAMENTE bars[N].tokens — nunca inventes figuras.',
    '2. Si creas un bloque "vincula" o rhythm_canvas match, enfrenta los símbolos reales detectados:',
    symbolLines,
    `   Lista global: [${summary.metrics.musicalSymbolsPresent.join(', ') || 'ninguno'}]`,
    '3. En bloques "arena" (cuestionario), haz preguntas analíticas sobre la partitura usando datos exactos del reporte.',
    `   Ejemplo: nota más aguda = ${summary.metrics.melodicRange.max} (${isoToChileanOctave(summary.metrics.melodicRange.max)}).`,
    `   Tonalidad detectada: ${summary.keySignature}. Clave: ${summary.clef === 'G' ? 'Sol' : 'Fa'}.`,
    `4. Toda referencia a sílabas rítmicas (${method}) debe corresponder EXACTAMENTE a los tokens de bars[].tokens.`,
    '5. PROHIBIDO: flechas ←, ecuaciones textuales, figuras no presentes, compases inventados, anglicismos (quarter note).',
    `6. Usa el título exacto de la obra: "${summary.title}" — no agregues instrumento (Flauta, Xilófono) ni texto del nombre de archivo.`,
    '7. Usa vocabulario chileno: negra, blanca, corchea, semicorchea, redonda, silencio de negra.',
    '',
    `[CONTEXTO] "${summary.title}" · ${summary.timeSignature} · ${summary.bpm} BPM · ${summary.totalBars} compases · figura dominante: ${summary.metrics.mostUsedFigure}`,
  ].join('\n');
}

export function hasLoadedRepertoire(
  scoreContext?: { summary?: ParsedScoreSummary } | null,
): boolean {
  return Boolean(
    scoreContext?.summary?.bars &&
    typeof scoreContext.summary.bars === 'object' &&
    Object.keys(scoreContext.summary.bars).length > 0,
  );
}

/** Orden de compases para rotación: hardest primero, luego el resto en orden numérico. */
export function getBarRotationOrder(summary: ParsedScoreSummary): number[] {
  const ts = summary.timeSignature;
  let barNums = Object.keys(summary.bars || {})
    .map(Number)
    .filter((n) => Number.isFinite(n) && n >= 1)
    .filter((n) => isMetricallyCompleteBar(summary.bars[n], ts))
    .sort((a, b) => a - b);
  if (!barNums.length) {
    barNums = Object.keys(summary.bars || {})
      .map(Number)
      .filter((n) => Number.isFinite(n) && n >= 1)
      .sort((a, b) => a - b);
  }
  if (!barNums.length) return [1];
  const hardest = summary.metrics.hardestBarNumber ?? barNums[0];
  if (!barNums.includes(hardest)) return barNums;
  return [hardest, ...barNums.filter((n) => n !== hardest)];
}

/** Compás asignado al n-ésimo bloque rítmico (0-based), ciclando sin repetir hasta agotar. */
export function getBarForRhythmBlockIndex(
  rhythmBlockIndex: number,
  summary: ParsedScoreSummary,
): number {
  const order = getBarRotationOrder(summary);
  return order[rhythmBlockIndex % order.length];
}

/** Modo con partitura: coacción estricta + elasticidad didáctica en el enfoque. */
export function buildRepertoireMusicPrompt(
  userPrompt: string,
  summary: ParsedScoreSummary,
  prefs?: Pick<MusicGenerationPrefs, 'readingMethod'>,
  focusKey = 'practical',
): string {
  const hardestNum = summary.metrics.hardestBarNumber;
  const secondBarNum = getBarForRhythmBlockIndex(1, summary);
  const exampleBar = summary.bars[hardestNum] ?? Object.values(summary.bars)[0];
  const secondBar = summary.bars[secondBarNum] ?? exampleBar;
  const exampleTokens = exampleBar?.tokens ?? ['E', 'E', 'Q'];
  const secondTokens = secondBar?.tokens ?? exampleTokens;
  const ts = summary.timeSignature;

  return [
    '[MODO ANÁLISIS DE REPERTORIO — COACCIÓN ESTRICTA]',
    'Hay partitura cargada. La verdad rítmica, melódica y métrica la dicta el archivo parseado — NO la IA.',
    'Deja de ser "creador musical": actúa como Ingeniero Didáctico.',
    'Concentra tu energía en redactar preguntas pedagógicas y distractores inteligentes alrededor de la obra REAL.',
    'Puedes variar el enfoque (identificar figuras, apreciación, historia, análisis) según la consigna del docente,',
    'pero NUNCA inventes compases, figuras o notas fuera del reporte técnico.',
    '',
    buildScoreEvidencePrompt(summary, prefs),
    '',
    '[UNIDAD DE CONTROL DE RESPUESTA JSON — FEW-SHOT OBLIGATORIO]',
    `Cuando el repertorio está en ${ts} (como "${summary.title}"), estructura bloques rítmicos EXACTAMENTE así.`,
    'PROHIBIDO usar 4/4 si el reporte dice otra métrica. PROHIBIDO inventar Redonda/Semicorchea si no están en el reporte.',
    '',
    '[EJEMPLO DE OUTPUT ESPERADO — COPIA ESTA FORMA]',
    JSON.stringify(
      {
        title: `Análisis rítmico de ${summary.title}`,
        subjectTag: 'musica',
        blocks: [
          {
            id: 'blk_ejemplo_1',
            index: 0,
            mode: 'rhythm_canvas',
            label: 'Identificación de compás complejo',
            payload: {
              title: 'Identificación de compás complejo',
              timeSignature: ts,
              targetBarNumber: hardestNum,
              originalBarTokens: exampleTokens,
              patternTokens: exampleTokens,
              exerciseType: 'identify_missing',
              taskKind: 'complete',
              missingIndex: Math.min(2, exampleTokens.length - 1),
              questionText: `Observa el compás ${hardestNum} de "${summary.title}". ¿Qué figura rítmica falta?`,
              prompt: `Observa el compás ${hardestNum} de "${summary.title}". ¿Qué figura rítmica falta?`,
              rhythmMethod: prefs?.readingMethod ?? 'tradicional',
            },
          },
          {
            id: 'blk_ejemplo_2',
            index: 1,
            mode: 'rhythm_canvas',
            label: 'Segundo compás de la pieza',
            payload: {
              title: `Compás ${secondBarNum} · ${summary.title}`,
              timeSignature: ts,
              targetBarNumber: secondBarNum,
              originalBarTokens: secondTokens,
              patternTokens: secondTokens,
              exerciseType: 'identify_missing',
              taskKind: 'complete',
              missingIndex: Math.min(1, secondTokens.length - 1),
              questionText: `Observa el compás ${secondBarNum} de "${summary.title}". ¿Qué figura rítmica falta?`,
              prompt: `Observa el compás ${secondBarNum} de "${summary.title}". ¿Qué figura rítmica falta?`,
              rhythmMethod: prefs?.readingMethod ?? 'tradicional',
            },
          },
        ],
      },
      null,
      2,
    ),
    '',
    `[CONSIGNA DOCENTE SOBRE LA OBRA]: "${String(userPrompt || '').trim()}"`,
    `[ENFOQUE SELECCIONADO]: ${focusKey}`,
  ].join('\n');
}
